import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useActiveGames, GAMES } from "@/lib/game";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CardScanner } from "@/components/CardScanner";
import { PayoutForm, ShippingForm } from "@/pages/SellerDashboard";
import { useFullLoader } from "@/components/loading/store";
import { versionOpts } from "@/components/publish/SearchResultsP2";
import { emptyDraft, itemError, isComplete, isSealedSel, MAX_ITEMS } from "@/lib/publishQueue";
import { usePublishQueue } from "@/hooks/publish/usePublishQueue";
import { usePublishSearch } from "@/hooks/publish/usePublishSearch";
import { useSellerPublishContext } from "@/hooks/publish/useSellerPublishContext";
import { useResultsKeyboard } from "@/hooks/publish/useResultsKeyboard";
import { useEnterKey } from "@/hooks/publish/useEnterKey";
import { usePhotoDropPaste } from "@/hooks/publish/usePhotoDropPaste";
import { useWideLayout } from "@/hooks/publish/useWideLayout";
import { Mono, BackPill, GamePills } from "./ui";
import { P1Inicio } from "./P1Inicio";
import { P2Resultados } from "./P2Resultados";
import { P3Ficha } from "./P3Ficha";
import { P4Cola, QueueList } from "./P4Cola";
import { P5Nuevo } from "./P5Nuevo";
import { P6Lote } from "./P6Lote";
import { P7Solicitudes } from "./P7Solicitudes";
import { D1Desktop } from "./D1Desktop";

const GAME_KEY = "hittcg_publish_game";
const PREFS_KEY = "hittcg_publish_prefs";
const readPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; } };
const savePrefs = (f) => { try { localStorage.setItem(PREFS_KEY, JSON.stringify({ language: f.language, condition: f.condition === "SEALED" ? "NM" : f.condition })); } catch { /* modo privado */ } };

const Modal = ({ open, onClose, title, testid, children }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="max-w-2xl bg-[#0A0A0A] border-[#262626] max-h-[90vh] overflow-y-auto" data-testid={testid}>
      <DialogHeader><DialogTitle className="text-white">{title}</DialogTitle></DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

export default function PublishV2() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const games = useActiveGames();
  const [game, setGameRaw] = useState(() => localStorage.getItem(GAME_KEY) || "pokemon");
  const [screen, setScreen] = useState(params.get("view") === "solicitudes" ? "p7" : "p1");
  const [type, setType] = useState("card");
  const [p5Kind, setP5Kind] = useState("card");
  const s = usePublishSearch(game);
  const ctx0 = useSellerPublishContext();
  const [shipOpen, setShipOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const ctx = { ...ctx0, onShipEdit: () => setShipOpen(true) };
  const [queueItems, setQueueItems] = useState([]);
  const queue = usePublishQueue({ drafts: queueItems, setDrafts: setQueueItems, game });
  const fresh = (g = game) => { const b = emptyDraft(); return { ...b, game: g, form: { ...b.form, ...readPrefs(), ship_cost: ctx0.defaultShip } }; };
  const [cur, setCur] = useState(() => fresh());
  const [justAdded, setJustAdded] = useState(null);
  const [restoredInfo, setRestoredInfo] = useState(null);
  const [slow, setSlow] = useState(false);
  const wide = useWideLayout();
  const wide1024 = useWideLayout("(min-width: 1024px)");
  const formRef = useRef(null);
  const searchRef = useRef(null);
  const queued = queueItems.filter((d) => d.selected);

  useEffect(() => { if (!GAMES[game]) setGameRaw("pokemon"); }, [game]);
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !queue.savedQueue?.length) { return; }
    restored.current = true;
    setRestoredInfo({ ids: queue.savedQueue.map((d) => d.id), at: queue.savedAt });
    setQueueItems((q) => { const saved = queue.savedAsDrafts(); const ids = new Set(saved.map((d) => d.id)); return [...saved, ...q.filter((d) => d.selected && !ids.has(d.id))]; });
    queue.setSavedQueue([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.savedQueue]);
  const prevQ = useRef(s.q);
  useEffect(() => {
    const changed = prevQ.current !== s.q;
    prevQ.current = s.q;
    if (changed && s.q.trim().length >= 2 && (screen === "p1" || screen === "p4")) setScreen("p2");
  }, [s.q, screen]);
  useEffect(() => { window.scrollTo(0, 0); }, [screen]);
  useEffect(() => {
    if (!queue.loading) { setSlow(false); return undefined; }
    const t = setTimeout(() => setSlow(true), 1500);
    return () => { clearTimeout(t); };
  }, [queue.loading]);
  useFullLoader(slow, { steps: "page" });

  const setGame = (g) => { localStorage.setItem(GAME_KEY, g); setGameRaw(g); s.reset(); setCur(fresh(g)); if (screen === "p2" || screen === "p3") setScreen("p1"); };
  const setForm = (patch) => setCur((d) => ({ ...d, error: null, form: { ...d.form, ...patch } }));
  const setPhotos = (files, paths) => setCur((d) => ({ ...d, photos: files, ...(paths ? { photo_paths: paths } : {}) }));

  const pickCard = (card, opts = {}) => {
    const sealed = isSealedSel(card);
    const printings = Array.isArray(card?.all_printings) ? card.all_printings : [];
    const solo = opts.printing || (game === "yugioh" && printings.length === 1 ? printings[0] : null);
    const selected = (!card.variants_available && card.versions?.length)
      ? { ...card, holo_card: card.holo_card || card.versions.some((v) => v.label === "Holo"), variants_available: Object.fromEntries(card.versions.map((v) => [v.key, true])) } : card;
    setCur((d) => ({
      ...d, game, selected, error: null,
      form: {
        ...d.form, ship_cost: ctx0.defaultShip,
        variant: sealed ? "sealed" : (opts.variant || "normal"), version_label: opts.label || "",
        condition: sealed ? "SEALED" : (d.form.condition === "SEALED" ? "NM" : d.form.condition || "NM"),
        yugioh_rarity: solo?.rarity || "", yugioh_set_code: solo?.set_code || "", first_edition: !!opts.first_edition,
        ...(opts.draft?.form || {}),
      },
      ...(opts.draft?.photos ? { photos: opts.draft.photos } : {}),
    }));
    setScreen("p3");
  };
  const pickWithVersion = (c, v) => pickCard(c, v ? versionOpts(game, v) : {});

  const addToQueue = () => {
    if (!cur.selected) { toast.error("Elige una carta primero"); return; }
    if (!(parseFloat(cur.form.price) >= 0.25)) { toast.error("Pon un precio (mínimo $0.25)"); return; }
    if (queued.length >= MAX_ITEMS) { toast.error(`Máximo ${MAX_ITEMS} en la cola`); return; }
    const item = { ...cur, error: null };
    setQueueItems((q) => [...q.filter((d) => d.selected), item]);
    queue.uploadInBackground(item, (n) => toast.error(`${n} foto(s) no se subieron · vuelve a agregarlas`));
    savePrefs(cur.form);
    setJustAdded(item);
    setCur(fresh());
    s.reset();
    setScreen("p4");
    setTimeout(() => searchRef.current?.focus(), 50);
  };
  const undo = () => { setQueueItems((q) => q.filter((d) => d.id !== justAdded.id)); setCur(justAdded); setJustAdded(null); setScreen("p3"); };
  const openItem = (d) => {
    setQueueItems((q) => [...q.filter((x) => x.id !== d.id && x.selected), ...(cur.selected ? [cur] : [])]);
    setCur(d); setJustAdded(null); setScreen("p3");
  };
  const removeItem = (d) => setQueueItems((q) => q.filter((x) => x.id !== d.id));

  const guard = (list) => {
    if (!ctx0.hasPayout) { setPayoutOpen(true); return false; }
    if (!ctx0.hasRates) { toast.error("Configura tu envío para publicar"); setShipOpen(true); return false; }
    const bad = list.find((d) => itemError(d, game));
    if (bad) { toast.error(`${bad.selected?.name || "Carta"}: ${itemError(bad, game)}`); return false; }
    return true;
  };
  const publishNow = async () => {
    if (!guard([cur])) return;
    const res = await queue.publish([cur]);
    if (!res) return;
    if (res.failed.length) { setCur((d) => ({ ...d, error: res.failed[0].error })); toast.error(res.text); return; }
    toast.success(res.text);
    savePrefs(cur.form);
    setCur(fresh()); s.reset();
    if (queued.length) setScreen("p4"); else nav("/seller");
  };
  const publishList = async (list) => {
    if (!list.length || !guard(list)) return;
    const res = await queue.publish(list);
    if (!res) return;
    if (res.failed.length) { toast[res.ok ? "warning" : "error"](res.text, { duration: 8000 }); return; }
    toast.success(res.text);
    const left = queued.filter((d) => !list.includes(d));
    if (!left.length && !cur.selected) { await queue.clearServer(); nav("/seller"); }
  };

  const kb = useResultsKeyboard({ results: s.results, enabled: wide && ["p1", "p2", "p3", "p4"].includes(screen), onPick: pickWithVersion, searchRef, resetKey: s.q });
  useEnterKey(formRef, addToQueue, wide && !!cur.selected);
  const photoDrop = usePhotoDropPaste((fs) => setCur((d) => ({ ...d, photos: [...d.photos, ...fs].slice(0, 12) })), !!cur.selected && screen === "p3");

  const onType = (t) => {
    setType(t);
    if (t === "lot") { setScreen("p6"); return; }
    if (t === "accessory") { setP5Kind("accessory"); setScreen("p5"); return; }
    setScreen(s.q.trim().length >= 2 ? "p2" : "p1");
    setTimeout(() => document.querySelector('[data-testid="p1-search"],[data-testid="d1-search"]')?.focus(), 50);
  };
  const newCard = () => { setP5Kind(type === "sealed" ? "sealed" : "card"); setScreen("p5"); };
  const again = (p) => pickCard(p.card, { variant: p.variant, draft: { form: { price: String(p.price), condition: p.condition, language: p.language, quantity: 1 } } });
  const publishRequest = (it) => pickCard({ id: it.linked_card_id || it.id, name: it.name_es, image_url: it.image_url, set_name: it.set_name, local_id: it.number, is_sealed: it.is_sealed, product_type: it.product_type },
    { draft: { form: { price: it.pub_draft?.price ? String(it.pub_draft.price) : "", condition: it.pub_draft?.condition || (it.is_sealed ? "SEALED" : "NM"), language: it.pub_draft?.language || "en", quantity: it.pub_draft?.quantity || 1 } } });

  const p1 = <P1Inicio games={games} game={game} onGame={setGame} type={type} onType={onType} q={s.q} setQ={s.setQ} onScan={() => setScanOpen(true)}
    recent={ctx0.recent} onAgain={again} inReview={ctx0.inReview} hasRequests={(ctx0.requests || []).length > 0} onRequests={() => setScreen("p7")}
    payoutMissing={ctx0.payout === null} onPayout={() => setPayoutOpen(true)}
    restored={restoredInfo && queued.some((d) => restoredInfo.ids.includes(d.id)) ? { n: queued.length, at: restoredInfo.at } : null}
    onViewQueue={() => setScreen("p4")}
    onDiscardQueue={() => { setQueueItems((q) => q.filter((d) => !restoredInfo.ids.includes(d.id))); setRestoredInfo(null); }} />;
  const p3 = cur.selected && <P3Ficha d={cur} setForm={setForm} setPhotos={setPhotos} game={game} ctx={ctx} onBack={() => setScreen(s.results.length ? "p2" : "p1")}
    onAddQueue={addToQueue} onPublishNow={publishNow} publishing={queue.loading} queueCount={queued.length} onQueue={() => { s.reset(); setScreen("p4"); }}
    onShipEdit={() => setShipOpen(true)} hasPayout={ctx0.hasPayout} onPayout={() => setPayoutOpen(true)} photoDrop={photoDrop} formRef={formRef} compact={wide} />;
  const qProps = { items: queued, game, onOpen: openItem, onRemove: removeItem, onRetry: (d) => publishList([d]), onPublishAll: () => publishList(queued.filter((d) => isComplete(d, game))),
    publishing: queue.loading, hasPayout: ctx0.hasPayout, onPayout: () => setPayoutOpen(true) };
  const desktop = wide && ["p1", "p2", "p3", "p4"].includes(screen);

  const desk2 = wide1024 && ["p5", "p6", "p7"].includes(screen);
  const body = desktop ? (
    <D1Desktop games={games} game={game} onGame={setGame} type={type} onType={onType} s={s} kb={kb} onPick={pickWithVersion} onNewCard={newCard} searchRef={searchRef}
      onScan={() => setScanOpen(true)} center={p3} right={<QueueList {...qProps} compact />}
      leftExtra={<div className="pt-2">{(ctx0.requests || []).length > 0 && <button type="button" onClick={() => setScreen("p7")} className="w-full text-left p-3 mb-3 rounded-[14px] border border-white/15 text-[13px] text-white" data-testid="d1-requests-link">Tus solicitudes · {(ctx0.requests || []).length}</button>}{ctx0.payout === null && <button type="button" onClick={() => setPayoutOpen(true)} className="w-full text-left p-3 mb-3 rounded-[14px] border border-[#FF4438]/60 bg-[#FF4438]/10 text-[13px] text-white" data-testid="d1-payout-notice">Agrega tus datos de cobro para poder publicar · <b className="text-[#FF6B5E]">Agregar</b></button>}
        <div className="text-[12.5px] text-white/45">Escribe nombre, número o set. ↑↓ recorre los resultados y ENTER elige la versión.</div></div>} />
  ) : screen === "p1" ? p1
    : screen === "p2" ? <P2Resultados s={s} game={game} onPick={pickCard} onNewCard={newCard} onClear={() => { s.reset(); setScreen("p1"); }} onScan={() => setScanOpen(true)} />
    : screen === "p3" ? (p3 || p1)
    : screen === "p4" ? <P4Cola justAdded={justAdded} onUndo={undo} q={s.q} setQ={s.setQ} {...qProps} />
    : screen === "p5" ? <P5Nuevo kind={p5Kind} game={game} initialName={p5Kind === "accessory" ? "" : s.q} suggestions={s.results} onPickSuggestion={(c) => pickCard(c)} onBack={() => setScreen(s.results.length ? "p2" : "p1")}
        ctx={ctx} hasPayout={ctx0.hasPayout} onPayout={() => setPayoutOpen(true)} onSent={() => { toast.success("Enviada · se publica sola al aprobarse"); ctx0.reloadRequests(); setScreen("p7"); }}
        onPublishExisting={(m, draft) => pickCard({ id: m.id, name: m.name_es, image_url: m.image_url, set_name: m.set_name, local_id: m.number }, { draft })} />
    : screen === "p6" ? <P6Lote game={game} ctx={ctx} onBack={() => { setType("card"); setScreen("p1"); }} hasPayout={ctx0.hasPayout} onPayout={() => setPayoutOpen(true)}
        onPublished={(p) => { toast.success(p.status === "pending_review" ? "Lote enviado · en revisión por cuenta nueva" : "Lote publicado"); nav("/seller"); }} />
    : <P7Solicitudes items={ctx0.requests} reload={ctx0.reloadRequests} onPublish={publishRequest} onView={(it) => nav(it?.linked_card_id || it?.id ? `/card/${it.linked_card_id || it.id}/${it.pub_draft?.language || it.language || "es"}` : "/seller")} onPayout={() => setPayoutOpen(true)} onBack={() => setScreen("p1")} />;

  return (
    <div className="sx min-h-screen bg-[#080808]" data-testid="publish-v2">
      <div className={`${desktop ? "max-w-[1440px] px-8" : desk2 ? "max-w-[1376px]" : "max-w-[460px] px-4"} mx-auto py-5`}>
        {desk2 ? (
          <div className="max-w-[1280px] mx-auto px-12 flex items-center justify-between mb-8" data-testid="pv2-header">
            <div className="flex items-center gap-3">
              <BackPill onClick={() => { setType("card"); setScreen("p1"); }} data-testid="pv2-desk-back">← Publicar</BackPill>
              <GamePills games={games} value={game} onChange={setGame} />
            </div>
            <button type="button" onClick={() => { s.reset(); setScreen("p4"); }} className="h-10 px-4 rounded-full border border-white/15 text-[13px] font-bold text-white" data-testid="pv2-header-queue">Cola · {queued.length}</button>
          </div>
        ) : (
        <div className="flex items-center justify-between mb-4" data-testid="pv2-header">
          <Mono tone="rgba(255,255,255,.55)">HIT · Publicar</Mono>
          <button type="button" onClick={() => { s.reset(); setScreen("p4"); }} className="h-8 px-3 rounded-full border border-white/15 text-[12px] font-bold text-white" data-testid="pv2-header-queue">Cola · {queued.length}</button>
        </div>
        )}
        {body}
      </div>
      <Modal open={payoutOpen} onClose={() => setPayoutOpen(false)} title="Datos de cobro" testid="pv2-payout-modal">
        <PayoutForm currentPayout={ctx0.payout} user={user} onSaved={async () => { await ctx0.reloadPayout(); setPayoutOpen(false); }} />
      </Modal>
      <Modal open={shipOpen} onClose={() => setShipOpen(false)} title="Tarifas de envío" testid="pv2-ship-modal">
        <ShippingForm currentConfig={ctx0.shipping} sellerProvince={user?.province} onSaved={async () => { await ctx0.reloadShipping(); setShipOpen(false); toast.success("Tarifas actualizadas"); }} />
      </Modal>
      {scanOpen && <CardScanner open={scanOpen} onClose={() => setScanOpen(false)} game={game} onCardDetected={(card) => { setScanOpen(false); pickCard(card); }} />}
    </div>
  );
}
